import React, { Fragment, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { CARD_SIDE } from '../../../../../../../services/cards/constants';
import useOpenAlert from '../../../../../../../hooks/useOpenAlert';
import DesignEditorLoadingIndicator from '../../../../../../../components/global/DesignEditor/components/DesignEditorLoadingIndicator';
import AccountCard from '../../../../../../../services/cards/components/AccountCard';
import NativeActivityIndicator from '../../../../../../../components/global/NativeActivityIndicator';
import { getErrorString } from '../../../../../../../util/normalize-errors';
import PrintBackgroundSelectorModal from '../../../../../../../services/prints/components/PrintBackgroundSelector';
import { getAccountCardElementsConfiguration } from '../../../../../../../services/cards/components/AccountCard/helpers';
import { isElementCardBackground } from '../../../../../../../components/global/CardDesigner/components/CardDesignerControlPanel/components/CardDesignerBackgroundControls/helpers';
import useDownloadConfiguration from '../../hooks/useDownloadConfiguration';
import { getPhotoOrientation } from '../../../../HierarchyItem/Matches/components/PurchasingOptions/components/PrintProductsGroup/helpers';

const propTypes = {
  defaultAccountCard: PropTypes.shape({
    id: PropTypes.string,
  }),
  defaultPhoto: PropTypes.shape({
    id: PropTypes.string,
  }),
  defaultPrintBackground: PropTypes.shape({
    id: PropTypes.string,
    asset_image: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  onAccountCardUpdated: PropTypes.func,
  printBackgrounds: PropTypes.arrayOf({
    id: PropTypes.string,
    asset_image: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  printBackgroundModalOpen: PropTypes.bool,
  setPrintBackgroundModalOpen: PropTypes.func,
  setLastUsedPrintBackground: PropTypes.func,
  className: PropTypes.string,
};

const DownloadsConfiguratorPreviewPane = ({
  defaultAccountCard,
  defaultPhoto,
  defaultPrintBackground,
  onAccountCardUpdated,
  printBackgrounds,
  printBackgroundModalOpen,
  setPrintBackgroundModalOpen,
  setLastUsedPrintBackground,
  className,
}) => {
  const openAlert = useOpenAlert();
  const [accountCardElementReferences, setAccountCardElementReferences] =
    useState({});

  const {
    printProduct,
    standardPrintProductConfiguration: { defaultPrintProductSize },
    accountCardConfiguration: {
      loadingAccountCard,
      accountCard,
      onElementConfigurationChange,
      cardElementLoading,
    },
  } = useDownloadConfiguration({
    defaultPhoto,
    defaultAccountCardId: defaultAccountCard?.id,
    defaultOrientation: getPhotoOrientation(defaultPhoto),
    onError: error => {
      openAlert({
        type: 'error',
        message:
          'It looks like an error ocurred while we were trying to configure your prints. Please start the configuration over.',
        info: getErrorString(error),
      });
    },
  });

  const state = useMemo(
    () => getAccountCardElementsConfiguration(accountCard, cardElementLoading),
    [accountCard, cardElementLoading],
  );

  const backgroundElement = useMemo(
    () =>
      state.find(e => e.side === CARD_SIDE.FRONT && isElementCardBackground(e)),
    [state],
  );

  useEffect(() => {
    if (defaultPrintProductSize?.id) {
      onAccountCardUpdated({
        printProduct,
        printProductSize: defaultPrintProductSize,
        accountCard,
        accountCardElementReferences,
      });
    }
  }, [
    printProduct?.id,
    defaultPrintProductSize?.id,
    accountCard?.id,
    // Listen to changes within account_card_sides
    // users should have valid configurations, hence why we
    // should update the account card sides whenever possible.
    accountCard?.account_card_sides,
    backgroundElement,
    accountCardElementReferences,
  ]);

  useEffect(() => {
    const setBackground = async () => {
      if (
        defaultPrintBackground &&
        !backgroundElement?.cardElementConfiguration?.asset_image?.id
      ) {
        await onElementConfigurationChange(backgroundElement, {
          photo_id: null,
          // We need to keep the element non-editable for it to work properly.
          is_editable: false,
          existing_asset_image_id: defaultPrintBackground?.asset_image?.id,
        });
      }
    };

    setBackground();
  }, [backgroundElement, defaultPrintBackground]);

  const onAddAccountCardElementReference = ({ cardElementId, ref }) => {
    setAccountCardElementReferences({
      ...accountCardElementReferences,
      [cardElementId]: ref,
    });
  };

  const classes = classNames({
    'is-loading': loadingAccountCard,
    [className]: !!className,
  });

  if (!printProduct) {
    return <DesignEditorLoadingIndicator />;
  }

  if (loadingAccountCard && !accountCard) {
    return (
      <div className={classes}>
        <NativeActivityIndicator />
      </div>
    );
  }

  return (
    <Fragment>
      {accountCard && (
        <Fragment>
          <AccountCard
            className="downloads-configurator-account-card"
            onElementConfigurationChange={async (element, config) => {
              await onElementConfigurationChange(element, config);
            }}
            onAddAccountCardElementReference={onAddAccountCardElementReference}
            accountCard={accountCard}
            cardElementLoading={cardElementLoading}
            allowPhotoChange={false}
            defaultPhoto={defaultPhoto}
          />
          <PrintBackgroundSelectorModal
            forceOpen={printBackgroundModalOpen}
            onClose={() => setPrintBackgroundModalOpen(false)}
            printBackgrounds={printBackgrounds}
            onPrintBackgroundSelect={async ({
              printBackground: newBackground,
            }) => {
              if (newBackground.asset_image) {
                await onElementConfigurationChange(backgroundElement, {
                  photo_id: null,
                  // We need to keep the element non-editable for it to work properly.
                  is_editable: false,
                  existing_asset_image_id: newBackground?.asset_image?.id,
                });
              }
              setLastUsedPrintBackground(newBackground);
              setPrintBackgroundModalOpen(false);
            }}
          />
        </Fragment>
      )}
    </Fragment>
  );
};

DownloadsConfiguratorPreviewPane.propTypes = propTypes;

export default DownloadsConfiguratorPreviewPane;
